import { useState } from "react";
import {
  View,
  Text,
  Flex,
  ActionButton,
  DialogTrigger,
  Dialog,
  Heading,
  Content,
  Tooltip,
  TooltipTrigger,
} from "@adobe/react-spectrum";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/storeConfig";
import MultiCallContainer from "./MultiCallContainer";
import {
  ConferenceIcon,
  MuteIcon,
  UnMmuteIcon,
  ForwardPhoneCallIcon,
  DtmfIcon,
  LeaveCall,
  PlayIcon,
  PauseIcon,
} from "../../common/Icons";
import CallTransferDialog from "../CallTransferDialog";
import ConferenceDialog from "./ConferenceDialog";
import DtmfDialog from "./DtmfDialog";
import Emitter from "../../../emitter";
import {
  API_CALL_HANGUP,
  API_CALL_REMOVE_CONNECTION,
  API_AGENT_GLOBAL_MUTE,
  CALLBACK,
  CHANNEL_TYPES,
  API_CALL_HOLD_OR_RESUME_AGENT_CONNECTION,
} from "../../../constants/constants";
import {
  getAgentContactById,
  getConnectionIdByPhoneNumber,
} from "../../../amazon-connect/api/call";
import { setCurrentSessionTab } from "../../../actions/session";
import storeRTK from "../../../store/storeRTK";

const CallInProgress = () => {
  const call = useSelector((state: RootState) => state.call);
  const conference = useSelector((state: RootState) => state.conference);
  const callback = useSelector((state: RootState) => state.callback);
  const voicemail = useSelector((state: RootState) => state.voicemail);
  const isAgentSelfCallHeld = useSelector((state: RootState) => state.session.isAgentSelfCallHeld);
  // Get global mute state from actual agent connection instead of Redux

  const [isTransferDialogOpen, setIsTransferDialogOpen] = useState(false);
  const [isConferenceDialogOpen, setIsConferenceDialogOpen] = useState(false);
  const isAgentGloballyMuted = useSelector((state: RootState) => state.session.isAgentGloballyMuted);

  const handleTransferCall = () => {
    if (call.isSilentMonitor || call.isBarge) return;
    setIsTransferDialogOpen(true);
  };

  const closeTransferDialog = () => {
    setIsTransferDialogOpen(false);
  };

  const closeConferenceDialog = () => {
    setIsConferenceDialogOpen(false);
  };

  const muteCallHandler = () => {
    if (call.isSilentMonitor && !call.isBarge) return;
    // Use global agent mute instead of per-call mute
    // Get current mute state from connection to determine toggle action
    Emitter.emit(API_AGENT_GLOBAL_MUTE, {
      body: { mute: !isAgentGloballyMuted },
    });
  };

  const hangupCallHandler = () => {
    if (conference && conference.id && call?.self?.state === "ESTABLISHED") {
      const primaryPhoneNumber =
        call?.customer?.phoneNumber || call?.customer?.displayName;
      const connectionId = getConnectionIdByPhoneNumber(primaryPhoneNumber);
      if (connectionId) {
        console.log(
          "CallInProgress: Removing primary connection with ID:",
          connectionId,
          "for phone:",
          primaryPhoneNumber
        );
        Emitter.emit(API_CALL_REMOVE_CONNECTION, {
          body: {
            connectionId,
            conversationId: call.id,
          },
        });
      } else {
        Emitter.emit(API_CALL_HANGUP, { body: { conversationId: call.id } });
      }
    } else {
      console.log(
        "CallInProgress: No conference or not established, using default hangup"
      );
      Emitter.emit(API_CALL_HANGUP, { body: { conversationId: call.id } });
      if (callback?.id)
        storeRTK.dispatch(setCurrentSessionTab({ id: CALLBACK }));
      if (voicemail?.id)
        storeRTK.dispatch(
          setCurrentSessionTab({ id: CHANNEL_TYPES.VOICEMAIL })
        );
    }
  };

  const holdOrplaySelfCallHandler = (isOnHold: Boolean) => {
    Emitter.emit(API_CALL_HOLD_OR_RESUME_AGENT_CONNECTION, { body: { conversationId: call.id, isOnHold } });
  };

  const bottomBarEndHandler = () => {
    // Agent leaves: end only agent's own connection on the current contact
    if ((conference?.id && call?.id) || call?.isBarge || conference?.isUnderSupervision) {
      try {
        const callContact = getAgentContactById(false, call.id);
        if (callContact && callContact.getAgentConnection) {
          callContact.getAgentConnection().destroy({
            success: function () {
              console.log(
                "CallInProgress: Agent left current contact (agent leg destroyed)"
              );
            },
            failure: function (err: any) {
              console.log(
                "CallInProgress: Failed to destroy agent leg on leave",
                err
              );
            },
          });
        } else {
          console.log(
            "CallInProgress: No specific contact found to leave; skipping global hangup"
          );
        }
      } catch (error) {
        console.log("CallInProgress: Error during leave", error);
      }
    } else {
      // Single call - end the call normally
      hangupCallHandler();
    }
  };

  const disableTransfer = (type?: string) => {
    const isSilentMonitor = call?.isSilentMonitor;
    const noTransferEndpoints = call?.allowedTransferEndpoints?.length === 0;
    const callConnected =
      (call?.self?.state || conference?.self?.state) === "ESTABLISHED";

    return type === "transfer"
      ? isSilentMonitor || noTransferEndpoints
      : !callConnected;
  };

  return (
    <>
      <Flex
        height={"100%"}
        width={"100%"}
        direction={"column"}
        justifyContent={"space-between"}
      >
        <View UNSAFE_className="ongoing-calls-wrapper">
          <View marginBottom={16}>
            <Text UNSAFE_className="ongoing-calls-heading" marginY={16}>
              {call?.id && conference?.id
                ? " Ongoing call(s)"
                : " Ongoing call"}
            </Text>
          </View>
          <MultiCallContainer primaryCall={call} />
        </View>

        {/* Bottom Action Bar */}
        <View
          backgroundColor="gray-100"
          borderWidth="thin"
          borderColor="gray-200"
          borderRadius="regular"
          marginX="2rem"
        >
          <Flex
            justifyContent="center"
            width="100%"
            gap="size-100"
            UNSAFE_style={{ padding: "1rem" }}
          >
            <Flex direction="column" alignItems="center" gap="size-50">
              <TooltipTrigger>
                <div style={{ display: 'inline-block' }}>
                  <DialogTrigger
                    type="popover"
                    hideArrow
                    isOpen={call?.id && conference?.id ? false : isConferenceDialogOpen}
                    onOpenChange={call?.id && conference?.id ? () => {} : setIsConferenceDialogOpen}
                  >
                    <ActionButton
                      UNSAFE_className="iconButton"
                      isQuiet
                      UNSAFE_style={{
                        cursor: call?.id && conference?.id ? "not-allowed" : "",
                        backgroundColor: "#eaeaea",
                        opacity: call?.id && conference?.id ? 0.5 : 1,
                      }}
                    >
                      <ConferenceIcon />
                    </ActionButton>
                    <Dialog UNSAFE_className="conference-dialog">
                      <Heading>
                        <Text UNSAFE_className="conference-heading">
                          Conference
                        </Text>
                      </Heading>
                      <Content marginTop={8}>
                        <ConferenceDialog onClose={closeConferenceDialog} />
                      </Content>
                    </Dialog>
                  </DialogTrigger>
                </div>
                <Tooltip>
                  {call?.id && conference?.id 
                    ? "Conference disabled"
                    : "Conference"
                  }
                </Tooltip>
              </TooltipTrigger>
              <Text UNSAFE_className="call-inProgress-font">Conference</Text>
            </Flex>

            <Flex direction="column" alignItems="center" gap="size-50">
              <DialogTrigger type="popover" hideArrow>
                <ActionButton UNSAFE_className="iconButton" isQuiet>
                  <DtmfIcon />
                </ActionButton>
                <Dialog UNSAFE_className="dtmf-dialog">
                  <Content>
                    <DtmfDialog />
                  </Content>
                </Dialog>
              </DialogTrigger>
              <Text UNSAFE_className="call-inProgress-font">DTMF</Text>
            </Flex>

            <Flex direction="column" alignItems="center" gap="size-50">
              <TooltipTrigger>
                <div style={{ display: 'inline-block' }}>
                  <ActionButton
                    UNSAFE_className="iconButton"
                    onPress={
                      (call.isSilentMonitor && !call.isBarge) || conference?.id
                        ? () => {}
                        : handleTransferCall
                    }
                    isQuiet
                    UNSAFE_style={{
                      cursor:
                        disableTransfer("transfer") || conference?.id
                          ? "not-allowed"
                          : "",
                      backgroundColor: "#eaeaea",
                      opacity:
                        (call.isSilentMonitor && !call.isBarge) || conference?.id
                          ? 0.5
                          : 1,
                    }}
                  >
                    <ForwardPhoneCallIcon />
                  </ActionButton>
                </div>
                <Tooltip>
                  {call.isSilentMonitor && !call.isBarge
                    ? "Transfer disabled"
                    : conference?.id
                      ? "Transfer disabled"
                      : "Transfer call"
                  }
                </Tooltip>
              </TooltipTrigger>
              <Text UNSAFE_className="call-inProgress-font">Transfer</Text>
            </Flex>
            <Flex direction="column" alignItems="center" gap="size-50">
              <TooltipTrigger>
                <div style={{ display: 'inline-block' }}>
                  <ActionButton
                    UNSAFE_className={
                      isAgentGloballyMuted ? "iconButton active " : "iconButton"
                    }
                    UNSAFE_style={{
                      cursor: (call.isSilentMonitor && !call.isBarge) || isAgentSelfCallHeld ? "not-allowed" : "",
                      backgroundColor: "#eaeaea",
                      opacity: (call.isSilentMonitor && !call.isBarge) || isAgentSelfCallHeld ? 0.5 : 1,
                    }}
                    onPress={
                      (call.isSilentMonitor && !call.isBarge) || isAgentSelfCallHeld
                        ? () => {}
                        : muteCallHandler
                    }
                    isQuiet
                  >
                    {isAgentGloballyMuted ? <MuteIcon /> : <UnMmuteIcon />}
                  </ActionButton>
                </div>
                <Tooltip>
                  {call.isSilentMonitor && !call.isBarge
                    ? "Mute disabled"
                    : isAgentSelfCallHeld
                      ? "Mute disabled"
                      : isAgentGloballyMuted
                        ? "Unmute yourself"
                        : "Mute yourself"
                  }
                </Tooltip>
              </TooltipTrigger>
              <Text UNSAFE_className="call-inProgress-font">
                {isAgentGloballyMuted ? "Unmute" : "Mute"}
              </Text>
            </Flex>
            {call?.id && conference?.id && isAgentSelfCallHeld &&
            <Flex direction="column" alignItems="center" gap="size-50">
              <TooltipTrigger>
                <div style={{ display: 'inline-block' }}>
                  <ActionButton
                    UNSAFE_className={"iconButton active "}
                    UNSAFE_style={{
                      cursor: conference?.isUnderSupervision ? "not-allowed" : "",
                      backgroundColor: "#eaeaea",
                      opacity: conference?.isUnderSupervision ? 0.5 : 1,
                    }}
                    onPress={conference?.isUnderSupervision ? () => {} : () => holdOrplaySelfCallHandler(true)}
                    isQuiet
                    isDisabled={conference?.isUnderSupervision}
                  >
                    <PlayIcon />
                  </ActionButton>
                </div>
                <Tooltip>
                  {conference?.isUnderSupervision
                    ? "Resume disabled - You are being barged"
                    : "Resume yourself"
                  }
                </Tooltip>
              </TooltipTrigger>
              <Text UNSAFE_className="call-inProgress-font">
                  Resume
              </Text>
            </Flex>}
            {call?.id && conference?.id && !isAgentSelfCallHeld &&
            <Flex direction="column" alignItems="center" gap="size-50">
              <TooltipTrigger>
                <div style={{ display: 'inline-block' }}>
                  <ActionButton
                    UNSAFE_className={"iconButton active "}
                    UNSAFE_style={{
                      cursor: conference?.isUnderSupervision ? "not-allowed" : "",
                      backgroundColor: "#eaeaea",
                      opacity: conference?.isUnderSupervision ? 0.5 : 1,
                    }}
                    onPress={conference?.isUnderSupervision ? () => {} : () => holdOrplaySelfCallHandler(false)}
                    isQuiet
                    isDisabled={conference?.isUnderSupervision}
                  >
                    <PauseIcon />
                  </ActionButton>
                </div>
                <Tooltip>
                  {conference?.isUnderSupervision
                    ? "Cannot hold yourself while being barged"
                    : "Hold yourself"
                  }
                </Tooltip>
              </TooltipTrigger>
              <Text UNSAFE_className="call-inProgress-font">
                  Hold
              </Text>
            </Flex>}

            <Flex direction="column" alignItems="center" gap="size-50">
              <TooltipTrigger>
                <div style={{ display: 'inline-block' }}>
                  <ActionButton
                    UNSAFE_className="call-end-Button"
                    onPress={conference?.id ? bottomBarEndHandler : () => {}}
                    isQuiet
                    UNSAFE_style={{
                      background: "red",
                      opacity: conference?.id ? 1 : 0.5,
                      cursor: conference?.id ? "pointer" : "not-allowed",
                    }}
                  >
                    <LeaveCall />
                  </ActionButton>
                </div>
                <Tooltip>
                  {!conference?.id
                    ? "End call disabled"
                    : call?.id && conference?.id 
                      ? "Leave call" 
                      : "End call"
                  }
                </Tooltip>
              </TooltipTrigger>
              <Text
                UNSAFE_className="call-inProgress-font"
                UNSAFE_style={{
                  opacity: conference?.id ? 1 : 0.5,
                }}
              >
                {call?.id && conference?.id ? "Leave" : "End"}
              </Text>
            </Flex>
          </Flex>
        </View>
      </Flex>

      {/* Transfer Dialog */}
      <CallTransferDialog
        isOpen={isTransferDialogOpen}
        onClose={closeTransferDialog}
        callDuration={call?.duration || 0}
      />
    </>
  );
};

export default CallInProgress;